import { useContext, useEffect, useState } from "react";
import { ToastContext } from "../../../../hooks/context";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import {
  cleanObject,
  extractDateOnly,
  truncateString,
} from "../../../../helpers";
import {
  useGetBillsLedgerAccount,
  useGetBillsItems,
  useGetPurchaseOrderDetails,
} from "../../../../hooks/api/queries";
import { useUploadImageMutation } from "../../../../hooks/api/mutations/useMedia";
import { useQueryClient } from "react-query";
import {
  useCreateBillMutation,
  useUpdateBillsMutation,
} from "../../../../hooks/api/mutations";

export const useBillsForm = ({ isEditMode = false, billsDetails = {} }) => {
  const triggerToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  const queryClient = useQueryClient();
  const [bankDetails, setBankDetails] = useState([]);

  const [billsForm, setBillsForm] = useState({
    po_id: billsDetails?.purchase_order?.id || search.get("po_id") || "",
    invoice_number: billsDetails?.invoice_number || "",
    invoice_date: billsDetails?.invoice_date
      ? extractDateOnly(billsDetails?.invoice_date)
      : "",
    invoice_due_date: billsDetails?.invoice_due_date
      ? extractDateOnly(billsDetails?.invoice_due_date)
      : "",
    invoice_amount: billsDetails?.invoice_amount || "",
    invoice_receipt_date: billsDetails?.invoice_receipt_date
      ? extractDateOnly(billsDetails?.invoice_receipt_date)
      : "",
    discount_amount: billsDetails?.discount_amount || "",
    amount_paid_to_supplier: billsDetails?.amount_paid_to_supplier || "",
    vat_rate: billsDetails?.vat_rate || "",
    account_name: billsDetails?.account_name || "",
    account_number: billsDetails?.account_number || "",
    bank_name: billsDetails?.bank_name || "",
    invoice_url: billsDetails?.invoice_url || null,
    invoice_urls:
      billsDetails?.invoice_urls?.map((item) => {
        return { url: item };
      }) || [],
    file_name: billsDetails?.invoice_url
      ? truncateString(billsDetails?.invoice_url, 20)
      : null,
    po_number:
      billsDetails?.purchase_order?.po_number || search.get("po_number") || "",
    payment_method: billsDetails?.payment_method || "",
    items: [],
  });

  const { data: billsItems } = useGetBillsItems({
    id: isEditMode ? id : billsForm?.po_id,
    isEditMode,
  });

  const { data: pODetails } = useGetPurchaseOrderDetails({
    id: billsForm?.po_id,
  });

  const { isLoading: isLoadingBankAccountsList, data: bankAccount } =
    useGetBillsLedgerAccount({
      queryParams: cleanObject({
        store_id:
          pODetails?.stores?.length === 1 ? pODetails?.stores?.[0]?.id : null,
        supplier_id: pODetails?.supplier?.id,
      }),
    });

  const { mutate: createBills, isLoading: isCreatingBills } =
    useCreateBillMutation();
  const { mutate: UpdateBills, isLoading: isUpdatingBills } =
    useUpdateBillsMutation({
      id: billsDetails?.id,
    });

  const { mutate: uploadInvoice, isLoading: isLoadingInvoiceFile } =
    useUploadImageMutation({});

  const getDataForSubmission = () => {
    return cleanObject(
      {
        ...billsForm,
        purchase_order_id: billsForm?.po_id,
        store_id: pODetails?.store?.id,
        supplier_id: pODetails?.supplier?.id,
        bill_items: billsForm?.items.map((item) =>
          cleanObject({
            billed_quantity: item?.bill_quantity,
            purchase_order_item_id: item?.po_item_id,
          }),
        ),
        ledger_account_splits: bankDetails
          ?.map((bank) => ({
            ledger_account_id: bank.value,
            amount: parseFloat(billsForm[`amount_${bank.value}`]),
          }))
          ?.filter(
            (split) =>
              split?.amount !== 0 &&
              split?.amount !== null &&
              !isNaN(split.amount),
          ),
      },
      ["po_id", "items"],
    );
  };

  const handleCreateBills = (status) => {
    // Logic to handle the creation of a Purchase Order
    if (billsForm?.items.length === 0)
      return triggerToast("Please add items to this bill", "warning");

    const data = getDataForSubmission();

    createBills(
      {
        ...data,
        invoice_urls: billsForm?.invoice_urls?.map((item) => item?.url),
        status,
      },
      {
        onSuccess: (res) => {
          triggerToast("Your bill has successfully been created", "success");
          const billsId = res?.data?.data?.id;
          const query = status === "IN_REVIEW" ? `?new_gr_id=${billsId}` : "";

          queryClient.invalidateQueries([
            "getPOBillsItems",
            `${billsForm?.po_id}`,
          ]);

          navigate(
            pathConstants.INVENTORY_BILLS_DETAILS({
              id: res?.data?.data?.id,
            }) + query,
          );
        },
      },
    );
  };

  const handleUpdateBills = (status) => {
    // Logic to handle the updating of a Purchase Order
    if (billsForm?.items.length === 0)
      return triggerToast("Please add items to the Goods Received", "warning");

    const data = getDataForSubmission();

    UpdateBills(
      {
        id: billsDetails?.id,
        data: {
          ...data,
          invoice_urls: billsForm?.invoice_urls?.map((item) => item?.url),
          status,
        },
      },
      {
        onSuccess: () => {
          triggerToast(
            `Bills: (${billsDetails?.bill_number}) updated successfully`,
            "success",
          );

          const query =
            status === "IN_REVIEW" ? `?new_gr_id=${billsDetails?.id}` : "";

          navigate(
            pathConstants.INVENTORY_BILLS_DETAILS({
              id: billsDetails?.id,
            }) + query,
          );
        },
      },
    );
  };

  const createOrUpdateBills = (status = "IN_REVIEW") => {
    if (isEditMode) handleUpdateBills(status);
    else handleCreateBills(status);
  };

  const handleUploadInvoice = (document, fileName) => {
    if (!document)
      return setBillsForm((prev) => ({
        ...prev,
        invoice_url: "",
        file_name: "",
      }));

    const formData = new FormData();
    formData.append("file", document);
    uploadInvoice(
      { formData },
      {
        onSuccess: (res) => {
          setBillsForm((prev) => ({
            ...prev,
            invoice_urls: [
              { url: res?.data?.data?.url, file_name: fileName },
              ...billsForm?.invoice_urls,
            ],
          }));
          triggerToast("Document uploaded successfully", "success");
        },
      },
    );
  };

  useEffect(() => {
    if (billsItems) setBillsForm((prev) => ({ ...prev, items: billsItems }));
  }, [billsItems]);

  useEffect(() => {
    if (!isEditMode || !billsDetails?.bill_ledger_account || !bankAccount?.data)
      return;

    const formattedBanks = billsDetails?.bill_ledger_account?.map((entry) => {
      const { id, account_name } = entry?.ledger_account;
      return { label: account_name, value: id };
    });

    setBankDetails(formattedBanks);

    const prefilledAmounts = billsDetails?.bill_ledger_account?.reduce(
      (acc, entry) => {
        const ledgerId = entry?.ledger_account?.id;
        acc[`amount_${ledgerId}`] = entry?.amount;
        return acc;
      },
      {},
    );

    setBillsForm((prev) => ({
      ...prev,
      ...prefilledAmounts,
    }));
  }, [isEditMode, billsDetails, bankAccount]);

  return {
    isSubmittingBills: isCreatingBills || isUpdatingBills,
    isLoadingInvoiceFile,
    billsForm,
    pODetails,
    setBillsForm,
    createOrUpdateBills,
    handleUploadInvoice,
    navigate,
    isLoadingBankAccountsList,
    bankAccount,
    bankDetails,
    setBankDetails,
  };
};
